"use client";

import { useT } from "@/hooks/useT";
import { type SmartActivityRow } from "@/hooks/useSmartActivityMap";

// Predexon outage: drop the SMART$/ROI cells entirely under the flag. The
// matching <col> (MarketsPageContent) and <th> (MarketsListTableHeader) are
// gated by the same flag, so the whole column disappears and the row stays
// aligned — identical to how non-PM sources render without these columns.
const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";

interface MarketsListSmartCellsProps {
  /** Smart-money row for this market, or null/undefined if missing. */
  row: SmartActivityRow | undefined | null;
  /** Outcome label shown in the tooltip — usually groupItemTitle / question / event title. */
  outcomeLabel: string;
}

/**
 * Two `<td>` cells (Smart Money + Smart ROI) for the markets list-view
 * row. Hides itself with empty-state pills when the row is missing or
 * has fewer than 5 smart wallets (the data is too thin to chart).
 *
 * Renders as a Fragment so it slots into a `<tr>` next to the other
 * cells without an extra wrapper. PM-only — the parent should gate
 * mounting with `effectiveSourceFilter === 'polymarket'`.
 */
export function MarketsListSmartCells({ row, outcomeLabel }: MarketsListSmartCellsProps) {
  const t = useT();
  const tooltipEarly = t("tooltip.smartMoneyEarly");

  // Predexon outage: render NO cells (placed after hooks). The matching
  // <col> and <th> are dropped in MarketsPageContent + MarketsListTableHeader
  // under the same flag, so the row stays aligned with two fewer columns —
  // the exact path non-PM sources already take.
  if (HIDE_PREDEXON) return null;

  // Empty / too-thin state — mirror the populated layout (bar + count
  // + dot + volume) so the column reads at the same visual width. All
  // slots show dashes in the same grey tone.
  if (!row || row.smartWalletCount < 5) {
    return (
      <>
        <td className="px-3 py-2.5" title={tooltipEarly}>
          <div className="flex items-center justify-center gap-1.5">
            <div className="relative w-14 h-1 rounded-[5px] bg-gray-200 dark:bg-white/10 overflow-hidden" />
            <span className="text-[10px] font-medium tabular-nums text-gray-400 dark:text-gray-600">N/A</span>
            <span className="text-[10px] font-medium tabular-nums text-gray-400 dark:text-gray-600">·</span>
            <span className="text-[10px] font-medium tabular-nums text-gray-400 dark:text-gray-600">N/A</span>
          </div>
        </td>
        <td className="px-3 py-2.5 text-right" title={tooltipEarly}>
          <span className="text-[12px] font-inter-tight font-medium tabular-nums text-gray-400 dark:text-gray-600">N/A</span>
        </td>
      </>
    );
  }

  const yesPct = Math.round(row.netBuyersPct * 100);
  const isYes = yesPct >= 50;
  const dominantPct = isYes ? yesPct : 100 - yesPct;
  const smartVol = row.smartVolume >= 1_000_000
    ? `$${(row.smartVolume / 1_000_000).toFixed(1)}M`
    : row.smartVolume >= 1000
      ? `$${(row.smartVolume / 1000).toFixed(0)}K`
      : `$${row.smartVolume.toFixed(0)}`;
  const tooltipLines = [
    outcomeLabel,
    t("smartMoney.wallets", { count: row.smartWalletCount }),
    t("smartMoney.netPositioning", { pct: dominantPct, side: isYes ? "YES" : "NO" }),
    t("smartMoney.volume", { volume: smartVol }),
    t("smartMoney.avgWinRate", { rate: `${(row.avgSmartWinRate * 100).toFixed(0)}%` }),
  ].join("\n");

  const roi = row.avgSmartRoi * 100;
  const up = roi >= 0;

  return (
    <>
      <td className="px-3 py-2.5" title={tooltipLines}>
        <div className="flex items-center justify-center gap-1.5">
          <div className="relative w-14 h-1 rounded-[5px] bg-gray-200 dark:bg-white/10 overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 ${isYes ? "bg-emerald-500" : "bg-red-500"}`}
              style={{ width: `${dominantPct}%` }}
            />
          </div>
          <span className="text-[10px] font-semibold tabular-nums text-gray-600 dark:text-gray-300">{row.smartWalletCount}</span>
          <span className="text-[10px] font-medium tabular-nums text-gray-500 dark:text-gray-500">·</span>
          <span className="text-[10px] font-medium tabular-nums text-gray-500 dark:text-gray-500">{smartVol}</span>
        </div>
      </td>
      <td className="px-3 py-2.5 text-right">
        <span className={`text-[12px] font-inter-tight font-semibold tabular-nums ${up ? "text-emerald-500" : "text-red-500"}`}>
          {up ? "+" : ""}{roi.toFixed(1)}%
        </span>
      </td>
    </>
  );
}
